import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Hook para proteger rutas del portal admin
 * @param {number} minAdminLevel - Nivel mínimo requerido (1, 2, 3)
 * @returns {boolean} - True si tiene acceso
 */
export const useAdminGuard = (minAdminLevel = 1) => {
  const navigate = useNavigate();

  useEffect(() => {
    const checkAccess = () => {
      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('token');

      // Verificar autenticación
      if (!storedUser || !storedToken) {
        navigate('/');
        return false;
      }

      try {
        const user = JSON.parse(storedUser);

        // Verificar si es admin (admins tienen isAdmin true)
        if (!user.isAdmin) {
          console.warn('❌ Acceso denegado: No es admin');
          navigate('/acceso-denegado');
          return false;
        }

        // Verificar AdminLevel
        const userLevel = Number(user.AdminLevel) || 1;
        if (userLevel < minAdminLevel) {
          console.warn(`❌ Acceso denegado: Nivel insuficiente (requiere ${minAdminLevel}, tiene ${userLevel})`);
          navigate('/acceso-denegado');
          return false;
        }

        return true;
      } catch (error) {
        console.error('Error checking admin access:', error);
        navigate('/');
        return false;
      }
    };

    checkAccess();
  }, [navigate, minAdminLevel]);

  return true;
};

/**
 * Componente HOC para proteger rutas
 * @param {React.Component} Component - Componente a proteger
 * @param {number} minAdminLevel - Nivel mínimo requerido
 */
export const withAdminGuard = (Component, minAdminLevel = 1) => {
  return (props) => {
    useAdminGuard(minAdminLevel);
    return <Component {...props} />;
  };
};

/**
 * Obtener nivel de admin del usuario actual
 * @returns {number} - AdminLevel (1, 2, 3)
 */
export const getAdminLevel = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return Number(user.AdminLevel) || 1;
  } catch {
    return 1;
  }
};

/**
 * Verificar si el usuario es admin
 * @returns {boolean}
 */
export const isAdmin = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.PortalRRHH === 1 || user.esAdmin === true;
  } catch {
    return false;
  }
};

/**
 * Verificar si el usuario tiene nivel de admin suficiente
 * @param {number} minLevel - Nivel mínimo requerido
 * @returns {boolean}
 */
export const hasAdminLevel = (minLevel) => {
  return getAdminLevel() >= minLevel;
};
